import React, { FunctionComponent, useEffect, useState } from 'react';
import { ascend, map, prop, sort } from 'ramda';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { debounce } from 'debounce';
import { Spinner } from 'react-bootstrap';
import { getPositionColumns } from '../../common/helpers/columnsHelper';
import { IBindingAction, IBindingCallback1 } from '../../common/models/callback';
import { IReferenceReqPosition } from '../../common/models/position/IReferenceReqPosition';
import Icon from '../../components/Icon';
import Input from '../../components/Input';
import RegularButton from '../../components/RegularButton';
import Table from '../../components/Table';
import { IPositionName } from '../../common/models/position/IPositionName';
import { IAppState } from '../../common/models/store/IAppState';
import { checkPositionNameRoutine, mergePositionsRoutine } from './routines';
import { usePrevious } from '../../common/helpers/customHooks';
import LoaderWrapper from '../../components/LoaderWrapper';
import ConfirmationModal from '../../components/ConfirmationModal';

interface IProps {
  positions: IReferenceReqPosition[];
  isNameTaken: boolean;
  isChecking: boolean;
  isSaving: boolean;
  error: string | null;
  checkName: IBindingCallback1<IPositionName>;
  mergePositions: IBindingCallback1<IPositionName>;
  onHide: IBindingAction;
}

const MergePositions: FunctionComponent<IProps> = ({
  error,
  isSaving,
  positions,
  isChecking,
  isNameTaken,
  onHide,
  checkName,
  mergePositions
}) => {
  const sortedPositions = sort(ascend(prop('lastUseDate')), positions);
  const [olderPosition] = sortedPositions;
  const [name, setName] = useState(olderPosition.name);
  const ids = map(({ id }) => id, sortedPositions);
  const search = debounce((v: string) => checkName({ name: v.trim(), ids }), 500);
  const [isConfirmShown, setIsConfirmShown] = useState(false);

  const onMerge = () => {
    setIsConfirmShown(false);
    mergePositions({ ids, name: name.trim() });
  };

  useEffect(() => {
    search(name);
    return () => search.clear();
  }, [name.trim()]);

  const prevIsSaving = usePrevious(isSaving);
  useEffect(() => {
    if (prevIsSaving && !isSaving && !error) {
      onHide();
    }
  }, [isSaving]);

  return (
    <LoaderWrapper loading={isSaving} transparent>
      <ConfirmationModal
        isShown={isConfirmShown}
        titleText={{ id: 'merge.confirmTitle', defaultMessage: 'Are you sure you want to merge?' }}
        descriptionText={{
          id: 'merge.confirmDescr',
          defaultMessage: 'Merging cannot be undone. Are you sure you want to merge these?'
        }}
        confirmBttnText={{ id: 'merge.confirmBttn', defaultMessage: 'Yes, merge' }}
        cancelBttnText={{ id: 'goBack', defaultMessage: 'No, go back' }}
        onCancel={() => setIsConfirmShown(false)}
        onConfirm={onMerge}
      />
      <div className="merge-modal">
        <header className="custom-modal-header">
          <FormattedMessage id="mergePositions.title" defaultMessage="Merge positions" />
        </header>
        <main className="flex-grow-1 d-flex flex-column">
          <div className="merge-info">
            <div className="position-title">
              <FormattedMessage
                id="mergePositions.description"
                defaultMessage="What should we call the merged position?"
              />
            </div>
            <div className="d-flex align-items-center mb-2">
              <Input
                className="w-50"
                size="lg"
                variant="underlined"
                onChange={setName}
                value={name}
                errorIntl={isNameTaken
                  ? { id: 'titleTaken', defaultMessage: 'Title already in use' }
                  : undefined}
              />
              {isChecking && <Spinner className="ml-3" size="sm" animation="border" />}
            </div>
          </div>
          <div className="merge-selected">
            <div className="merge-selected-title">
              <FormattedMessage id="mergePositions.selected" defaultMessage="Selected positions" />
              <div className="number-badge">{positions.length}</div>
            </div>
            <Table
              config={{
                data: { items: sortedPositions, total: positions.length },
                columns: getPositionColumns()
              }}
            />
          </div>
        </main>
        <footer className="modal-footer-bttns">
          <RegularButton
            textIntlId="cancel"
            defaultMessage="Cancel"
            onClick={onHide}
            variant="tertiary"
          />
          <RegularButton
            textIntlId="iconButtonLable.merge"
            defaultMessage="Merge"
            icon={<Icon icon="merge" />}
            disabled={!name.trim() || isNameTaken || isChecking || isSaving}
            onClick={() => setIsConfirmShown(true)}
          />
        </footer>
      </div>
    </LoaderWrapper>
  );
};

const mapStateToProps = ({
  mergePositions: { isChecking, isNameTaken, isSaving, error }
}: IAppState) => ({ isChecking, isNameTaken, isSaving, error });

const mapDispatchToProps = {
  mergePositions: mergePositionsRoutine,
  checkName: checkPositionNameRoutine
};

export default connect(mapStateToProps, mapDispatchToProps)(MergePositions);
